import React from 'react';
import { motion } from 'framer-motion';
import { Wind, Zap, Shield, TrendingUp, Wallet, Search, CheckCircle, BarChart3, Users, Globe, Coins } from 'lucide-react';
import { useWallet } from '../context/SimpleWalletContext';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useNavigate } from 'react-router-dom';

const CleanBackground = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none bg-gray-50">
      {/* Subtle geometric pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gray-300 rounded-full transform rotate-12"></div>
        <div className="absolute top-1/3 right-1/3 w-40 h-40 bg-gray-300 rounded-full transform -rotate-6"></div>
        <div className="absolute bottom-1/3 left-1/2 w-36 h-36 bg-gray-300 rounded-full transform rotate-3"></div>
        <div className="absolute top-1/2 right-1/4 w-28 h-28 bg-gray-300 rounded-full transform -rotate-12"></div>
      </div>
    </div>
  );
};

export default function HowItWorks() {
  const { connect, connected, publicKey } = useWallet();
  const { user } = useFirebaseAuth();
  const navigate = useNavigate();

  const handleWalletConnect = async () => {
    if (!user) {
      // Redirect to login if user is not authenticated
      navigate('/login');
      return;
    }

    try {
      await connect();
      // Wallet connection will be handled by the wallet context
    } catch (error: any) {
      alert('Failed to connect wallet: ' + (error?.message || 'Unknown error'));
    }
  };

  const handleStepClick = (stepNumber: string, stepTitle: string) => {
    switch (stepNumber) {
      case '01':
        // Step 1: Connect Wallet
        if (!user) {
          navigate('/login');
        } else if (!connected) {
          handleWalletConnect();
        } else {
          alert('✅ Wallet already connected! You can now explore projects.');
        }
        break;
      case '02':
        // Step 2: Explore Projects
        if (!user) {
          navigate('/login');
        } else {
          navigate('/features'); // This route shows PublicProjects component
        }
        break;
      case '03':
        // Step 3: Make Investment
        if (!user) {
          navigate('/login');
        } else {
          navigate('/projects'); // This route shows Projects component for investments
        }
        break;
      case '04':
        // Step 4: Track Returns
        if (!user) {
          navigate('/login');
        } else {
          navigate('/dashboard'); // This route shows Dashboard with performance tracking
        }
        break;
      default:
        // All steps are properly handled above
        break;
    }
  };

  const steps = [
    {
      number: "01",
      title: "CONNECT_WALLET",
      subtitle: "Blockchain Authentication",
      description: "Connect your Solana wallet to securely access the platform. Our system uses advanced cryptographic protocols to ensure your identity and funds remain safe.",
      icon: Wallet,
      color: "var(--cyber-blue)",
      features: ["Multi-wallet support", "Hardware wallet compatible", "Biometric authentication", "Zero-knowledge proofs"]
    },
    {
      number: "02",
      title: "EXPLORE_PROJECTS",
      subtitle: "Verified Investment Opportunities",
      description: "Browse through our curated portfolio of renewable energy projects. Each project undergoes rigorous due diligence and real-time monitoring.",
      icon: Search,
      color: "var(--eco-green)",
      features: ["AI-powered matching", "Risk assessment", "Environmental impact data", "Real-time analytics"]
    },
    {
      number: "03",
      title: "MAKE_INVESTMENT",
      subtitle: "Smart Contract Execution",
      description: "Invest with confidence using our automated smart contracts. Your investment is tokenized and recorded immutably on the Solana blockchain.",
      icon: Coins,
      color: "var(--neon-green)",
      features: ["Instant execution", "Fractional ownership", "Transparent fees", "Automated compliance"]
    },
    {
      number: "04",
      title: "TRACK_RETURNS",
      subtitle: "Real-time Performance",
      description: "Monitor your investment performance in real-time. Receive automated returns directly to your wallet as projects generate clean energy revenue.",
      icon: BarChart3,
      color: "var(--electric-purple)",
      features: ["Live dashboard", "Automated payouts", "Performance analytics", "Carbon credit tracking"]
    }
  ];

  const benefits = [
    {
      icon: Shield,
      title: "SECURE_&_TRANSPARENT",
      description: "All transactions are recorded on-chain with military-grade encryption",
      color: "var(--cyber-blue)"
    },
    {
      icon: TrendingUp,
      title: "COMPETITIVE_RETURNS",
      description: "Earn up to 15% annually while supporting renewable energy",
      color: "var(--neon-green)"
    },
    {
      icon: Globe,
      title: "GLOBAL_IMPACT",
      description: "Contribute to reducing global carbon emissions by 50K+ kg annually",
      color: "var(--eco-green)"
    },
    {
      icon: Users,
      title: "COMMUNITY_DRIVEN",
      description: "Join 1,200+ investors building a sustainable future together",
      color: "var(--electric-purple)"
    }
  ];

  return (
    <div className="min-h-screen relative bg-gray-50">
      <CleanBackground />

      <div className="relative z-10 container mx-auto px-4 py-20">
        {/* Header Section */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="flex items-center justify-center mb-8"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
          >
            <div className="relative">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-lg">
                <Wind className="w-12 h-12 text-green-600" />
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center bg-green-600">
                <Zap className="w-3 h-3 text-white" />
              </div>
            </div>
          </motion.div>

          <motion.h1
            className="text-4xl md:text-5xl font-bold mb-6 text-gray-900"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            How It <span className="text-green-600">Works</span>
          </motion.h1>

          <motion.p
            className="text-lg max-w-3xl mx-auto leading-relaxed text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Discover how GreenOVA8 revolutionizes renewable energy investments through blockchain technology.
            <br />
            <span className="text-green-600 font-medium">
              Simple • Secure • Sustainable
            </span>
          </motion.p>
        </motion.div>

        {/* Simple Process Overview */}
        <motion.div
          className="mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Simple <span className="text-green-600">Process</span>
            </h2>
            <p className="text-lg max-w-3xl mx-auto text-gray-600">
              Four easy steps to start investing in renewable energy projects
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
            {/* Process Steps Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg text-center border border-gray-200">
                <div className="text-xs font-medium text-green-600 mb-1">STEP 1</div>
                <div className="text-sm font-bold text-gray-900">Connect</div>
                <div className="text-xs text-gray-500">Wallet</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg text-center border border-gray-200">
                <div className="text-xs font-medium text-blue-600 mb-1">STEP 2</div>
                <div className="text-sm font-bold text-gray-900">Explore</div>
                <div className="text-xs text-gray-500">Projects</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg text-center border border-gray-200">
                <div className="text-xs font-medium text-purple-600 mb-1">STEP 3</div>
                <div className="text-sm font-bold text-gray-900">Invest</div>
                <div className="text-xs text-gray-500">Secure</div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg text-center border border-gray-200">
                <div className="text-xs font-medium text-yellow-600 mb-1">STEP 4</div>
                <div className="text-sm font-bold text-gray-900">Track</div>
                <div className="text-xs text-gray-500">Returns</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Process Steps */}
        <div className="mb-20">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isEven = index % 2 === 0;

            return (
              <motion.div
                key={index}
                className="relative mb-20 last:mb-0"
                initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + index * 0.2, duration: 0.6 }}
              >
                <div className={`grid md:grid-cols-2 gap-16 items-center ${isEven ? '' : 'md:grid-flow-col-dense'}`}>
                  {/* Content */}
                  <div className={isEven ? '' : 'md:col-start-2'}>
                    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
                      <div className="flex items-center mb-6">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                          <Icon className="w-8 h-8 text-green-600" />
                        </div>
                        <div>
                          <div className="text-xs font-medium uppercase tracking-widest mb-1 text-gray-500">
                            STEP {step.number}
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900">
                            {step.title.replace(/_/g, ' ')}
                          </h3>
                          <div className="text-sm text-green-600">
                            {step.subtitle}
                          </div>
                        </div>
                      </div>

                      <p className="text-lg leading-relaxed mb-6 text-gray-600">
                        {step.description}
                      </p>

                      <div className="grid grid-cols-2 gap-4">
                        {step.features.map((feature, fIndex) => (
                          <div key={fIndex} className="flex items-center">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                            <span className="text-sm text-gray-600">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Visual Element */}
                  <div className={isEven ? '' : 'md:col-start-1'}>
                    <div className="relative w-full max-w-md mx-auto">
                      <motion.div
                        className="p-4 md:p-6 text-center cursor-pointer transition-all duration-300 hover:scale-105 rounded-2xl overflow-hidden relative bg-white border border-gray-200 shadow-lg"
                        whileHover={{
                          scale: 1.02,
                          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                        }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleStepClick(step.number, step.title)}
                      >
                        <div className="relative w-full flex flex-col items-center justify-center" style={{ minHeight: '160px', maxHeight: '180px' }}>
                          {/* Simple step number with minimal effects */}
                          <div className="relative z-20 mb-4">
                            <div
                              className="text-5xl md:text-6xl lg:text-7xl font-bold select-none text-green-600"
                              style={{
                                lineHeight: '1',
                                textAlign: 'center'
                              }}
                            >
                              {step.number}
                            </div>
                          </div>

                          {/* Simple circular border */}
                          <div className="absolute inset-0 flex items-center justify-center z-10">
                            <div
                              className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 border-2 border-dashed rounded-full border-green-600 opacity-60"
                            ></div>
                          </div>

                          {/* Simple background glow */}
                          <div className="absolute inset-0 flex items-center justify-center opacity-20 z-5 bg-green-50 rounded-full"></div>
                        </div>

                        {/* Simple step button */}
                        <div className="mt-4 relative z-20 flex justify-center">
                          <div className="text-sm font-medium px-4 py-2 rounded-full text-center transition-all duration-300 hover:scale-105 bg-green-600 text-white border border-green-600 min-w-[140px]">
                            Step {step.number} - Click Me
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* Connection line (not on last item) */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 mt-8">
                    <div className="w-0.5 h-16 mx-auto bg-gray-300"></div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Benefits Section */}
        <motion.div
          className="bg-white p-8 md:p-12 mb-20 rounded-2xl shadow-lg border border-gray-200"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-gray-900">
            Why Choose <span className="text-green-600">GreenOVA8</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.8 + index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-gray-900">
                    {benefit.title.replace(/_/g, ' ')}
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-600">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="bg-white p-8 md:p-12 text-center rounded-2xl shadow-lg border border-gray-200"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.0 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            Ready to <span className="text-green-600">Start?</span>
          </h2>

          <p className="text-lg max-w-2xl mx-auto mb-8 text-gray-600">
            Join thousands of investors who are already earning returns while building a sustainable future.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              className="bg-green-600 text-white px-8 py-4 rounded-lg font-medium flex items-center justify-center hover:bg-green-700 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleWalletConnect}
            >
              <Zap className="w-5 h-5 mr-2" />
              {connected ? `Connected (${publicKey?.toBase58().slice(0, 6)}...)` : 'Connect Wallet'}
            </motion.button>

            <motion.button
              className="bg-gray-100 text-gray-700 px-8 py-4 rounded-lg font-medium flex items-center justify-center hover:bg-gray-200 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Shield className="w-5 h-5 mr-2" />
              Learn More
            </motion.button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-8 max-w-lg mx-auto">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">25+</div>
              <div className="text-xs font-medium uppercase text-gray-500">Projects</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">$2.5M</div>
              <div className="text-xs font-medium uppercase text-gray-500">Invested</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">1.2K+</div>
              <div className="text-xs font-medium uppercase text-gray-500">Investors</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
